import { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useDebounce } from 'use-debounce';
import {
  type OrganisationIdentity,
  OrganisationIdentityUpdateSchema,
  type OrganisationIdentityFormValues,
} from '@duke-hq/svc-api/admin/trpc/routers/organisation/schemas';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { ColourField } from './ColourField';
import { EmailPreview } from './EmailPreview';
import { type FieldDefinition, IDENTITY_GROUPS } from './sections';
import { toEmailBrand } from './toEmailBrand';
import { testIds } from './testIds';

function TextField({
  name,
  label,
  helper,
  isRequired,
}: {
  name: keyof OrganisationIdentityFormValues;
  label: string;
  helper?: string;
  isRequired?: boolean;
}): React.ReactElement {
  const {
    register,
    formState: { errors },
  } = useFormContext<OrganisationIdentityFormValues>();

  return (
    <FormControl isInvalid={errors[name] != null} isRequired={isRequired}>
      <FormLabel fontSize="sm" mb={1}>
        {label}
      </FormLabel>
      <Input {...register(name)} size="sm" />
      {helper != null && errors[name] == null && (
        <FormHelperText fontSize="xs">{helper}</FormHelperText>
      )}
      <FormErrorMessage fontSize="xs">{errors[name]?.message}</FormErrorMessage>
    </FormControl>
  );
}

/**
 * The preview, following the form rather than the saved row.
 *
 * Its own component so a keystroke re-renders this and not the fields, and
 * debounced because the iframe is handed a whole document: replacing `srcDoc`
 * reloads it from scratch, which on every keystroke blanks the frame and throws
 * away the reader's scroll position.
 */
function LivePreview({ identity }: { identity: OrganisationIdentity }): React.ReactElement {
  const { control } = useFormContext<OrganisationIdentityFormValues>();
  const draft = useWatch({ control });
  const [settled] = useDebounce(draft, 300);
  const brand = useMemo(() => toEmailBrand(identity, settled), [identity, settled]);

  return <EmailPreview brand={brand} />;
}

function valuesFor(
  identity: OrganisationIdentity,
  fields: FieldDefinition[],
): OrganisationIdentityFormValues {
  const values: OrganisationIdentityFormValues = {};
  for (const { name } of fields) {
    values[name] = identity[name] ?? '';
  }
  return values;
}

/** Every field on one form, so one Save writes a consistent identity. */
function copy<K extends keyof OrganisationIdentityFormValues>(
  into: OrganisationIdentityFormValues,
  from: OrganisationIdentityFormValues,
  key: K,
): void {
  into[key] = from[key];
}

/**
 * Only the fields this admin edited. Sending all of them would have the save
 * write back what the form loaded, so a value another admin changed in the
 * meantime would be reverted by someone who never touched that field.
 */
function changedOnly(
  submitted: OrganisationIdentityFormValues,
  dirtyFields: Partial<Record<keyof OrganisationIdentityFormValues, boolean>>,
  fields: FieldDefinition[],
): OrganisationIdentityFormValues {
  const changes: OrganisationIdentityFormValues = {};
  for (const { name } of fields) {
    if (dirtyFields[name] === true) {
      // Generic in the key so both sides are the same field's type; indexing with
      // the bare union widens the write and rejects the nullable fields.
      copy(changes, submitted, name);
    }
  }
  return changes;
}

export function IdentityForm({ identity }: { identity: OrganisationIdentity }): React.ReactElement {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const groups = IDENTITY_GROUPS();
  const fields = groups.flatMap((group) => group.fields);

  const methods = useForm<OrganisationIdentityFormValues>({
    resolver: zodResolver(OrganisationIdentityUpdateSchema),
    reValidateMode: 'onSubmit',
    // `values` rather than `defaultValues` so a refetch replaces what the form
    // holds, instead of the next save writing stale values back over it.
    values: valuesFor(identity, fields),
  });

  const update = trpcAdmin.organisation.identity.update.useMutation({
    onSuccess: async () => {
      await utils.organisation.identity.get.invalidate();
      toast({ status: 'success', title: t`Saved` });
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Could not save`, description: error.message });
    },
  });

  const { formState } = methods;

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit((submitted) =>
          update.mutate(changedOnly(submitted, formState.dirtyFields, fields)),
        )}
        data-testid={testIds.identityForm}
        noValidate
      >
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} alignItems="start">
          <Stack gap={4}>
            {groups.map((group) => (
              <Card key={group.title} borderWidth="1px" borderColor="border.default" shadow="none">
                <CardBody>
                  <Stack gap={4}>
                    <Stack gap={1}>
                      <Heading fontSize="md" size="xs">
                        {group.title}
                      </Heading>
                      <Text fontSize="sm" color="fg.muted">
                        {group.description}
                      </Text>
                    </Stack>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      {group.fields.map((field) =>
                        field.kind === 'colour' ? (
                          <ColourField
                            key={field.name}
                            name={field.name}
                            label={field.label}
                            helper={field.helper}
                          />
                        ) : (
                          <TextField
                            key={field.name}
                            name={field.name}
                            label={field.label}
                            helper={field.helper}
                            isRequired={field.isRequired}
                          />
                        ),
                      )}
                    </SimpleGrid>
                  </Stack>
                </CardBody>
              </Card>
            ))}
          </Stack>

          {/* Sticky so the email stays in view while the fields above it scroll. */}
          <Box position="sticky" top={4}>
            <LivePreview identity={identity} />
          </Box>
        </SimpleGrid>

        <HStack justifyContent="flex-end" position="sticky" bottom={0} py={3} bg="bg.surface">
          <Button
            type="submit"
            variant="primary"
            isLoading={update.isPending}
            isDisabled={!formState.isDirty}
            data-testid={testIds.saveButton}
          >
            <Trans>Save</Trans>
          </Button>
        </HStack>
      </form>
    </FormProvider>
  );
}
